import { useEffect, useRef } from "react"
import { useSelector } from "react-redux"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import { isConcierge } from "../../users/accountRole"
import {
  selectActiveAccountId,
  selectProfile,
  selectToken,
} from "../../users/reducer"

const HEARTBEAT_INTERVAL_MS = 60_000
const IDLE_TIMEOUT_MS = 5 * 60_000

const ACTIVITY_EVENTS = [
  "mousedown",
  "keydown",
  "touchstart",
  "scroll",
  "click",
]

function isTabVisible() {
  return (
    typeof document === "undefined" || document.visibilityState === "visible"
  )
}

export default function ConciergeActiveHeartbeat() {
  const axios = useAuthorizedAxiosClient()
  const token = useSelector(selectToken)
  const profile = useSelector(selectProfile)
  const activeAccountId = useSelector(selectActiveAccountId)
  const lastActivityAtRef = useRef(Date.now())
  const enabled =
    Boolean(token) && isConcierge(profile) && Boolean(activeAccountId)

  useEffect(() => {
    if (!enabled) {
      return undefined
    }

    function markActive() {
      lastActivityAtRef.current = Date.now()
    }

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, markActive, { passive: true })
    })

    function sendHeartbeat() {
      if (!isTabVisible()) {
        return
      }
      if (Date.now() - lastActivityAtRef.current > IDLE_TIMEOUT_MS) {
        return
      }
      axios
        .post("/clients/api/concierge/daily-activity/active-heartbeat/")
        .catch(() => {})
    }

    sendHeartbeat()
    const intervalId = window.setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS)

    function handleVisibilityChange() {
      if (isTabVisible()) {
        markActive()
        sendHeartbeat()
      }
    }
    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      window.clearInterval(intervalId)
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, markActive)
      })
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [axios, enabled])

  return null
}
